import * as PropTypes from 'prop-types';

import {Owner, OwnerInfo, Permissions, PERMISSIONS_SHAPE} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {BatchSummary} from 'constants/SampleConstants';
import {ProjectSummary, SourceSummary} from 'constants/SourceConstants';
import {User, USER_SHAPE_PROPS} from 'constants/UserConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {StructuredData} from 'notebook/data-tables/DataTableConstants';
import {EntryDaySummary, EntryVersion, FromServerId, ServerEntryVersion} from 'notebook/ELNConstants';
import {Review} from 'notebook/reviews/ReviewConstants';
import {TransferOperation, TransferOperationList} from 'notebook/transfer-tables/TransferTableConstants';
import {SampleLinkSchemaField, SCHEMA_FIELD_SHAPE} from 'schemas/fields/SchemaFieldConstants';
import {AnonymousSchema} from 'schemas/SchemaConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {Optional} from 'util/Types';
import {STAGE_RUN_STATUS} from 'workflows/constants/StageRunConstants';

import {FolderSummary} from '../constants/FolderConstants';

type Tag = FileConstants.Tag;

export const FILE_TYPES_WITH_MODAL_EDITOR = [ModelTypeConstants.STAGE_CONFIGURATION];

export const WORKFLOW_STATUS_DRAFT = 'DRAFT'; // Keep in sync with workflow.py

export const CONTAINER_COLUMN_LABEL = 'Container';

export const SAMPLE_COLUMN_LABEL = 'Sample ID';

export const STAGE_RUN_COLUMN_LABEL = 'Stage Run';

export enum WorkflowScreen {
  WORKFLOW_DASHBOARD = 'WORKFLOW_DASHBOARD',
  WORKFLOW_DASHBOARD_TEMPLATES = 'WORKFLOW_DASHBOARD_TEMPLATES',
  WORKFLOW_DETAILS = 'WORKFLOW_DETAILS',
  WORKFLOW_TEMPLATE_VIEW = 'WORKFLOW_TEMPLATE_VIEW',
}

export const WORKFLOW_SCREEN = {
  WORKFLOW_DASHBOARD: 'WORKFLOW_DASHBOARD',
  WORKFLOW_DASHBOARD_TEMPLATES: 'WORKFLOW_DASHBOARD_TEMPLATES',
  WORKFLOW_DETAILS: 'WORKFLOW_DETAILS',
  WORKFLOW_TEMPLATE_VIEW: 'WORKFLOW_TEMPLATE_VIEW',
};

export const WORKFLOW_FILTER_ANY = 'ANY';

export const WORKFLOW_FILTER_NONE = 'NONE';

export const WORKFLOW_STATUSES = [
  WORKFLOW_STATUS_DRAFT,
  STAGE_RUN_STATUS.INITIALIZED,
  STAGE_RUN_STATUS.COMPLETED,
];

export const WORKFLOW_STATUS_DISPLAY_NAMES: {[status: string]: string} = {
  [STAGE_RUN_STATUS.INITIALIZED]: 'In progress',
  [STAGE_RUN_STATUS.COMPLETED]: 'Completed',
  [WORKFLOW_STATUS_DRAFT]: 'Draft',
};

// TODO(TypeScript): rename to CamelCase
export enum INPUT_TABLE_SORT_BY_TYPE {
  DEFAULT = 'DEFAULT',
  SAMPLE = 'SAMPLE',
  CONTAINER = 'CONTAINER',
  STAGE_RUN = 'STAGE_RUN',
  EXPERIMENTAL_CONDITION_VALUE = 'EXPERIMENTAL_CONDITION_VALUE',
}

// Note that when in configuration mode the screen is not collapsible,
// so we have to manually expand it.
export const COLLAPSIBLE_WORKFLOW_SCREENS = [WORKFLOW_SCREEN.WORKFLOW_DETAILS];

export const BATCH_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface StageRunBioentity {
  id: string;
  displayLabel: string;
}
export interface StageRunSample {
  // TODO(TypeScript): handle cases where id is null
  id: string;
  name: string;
  bioentity?: Optional<StageRunBioentity>;
  // If the user doesn't have permissions for the batch or it was deleted, batch and url are undefined.
  batch?: Optional<BatchSummary>;
  fileId?: string | null;
  containerId?: string | null;
  url: string | null;
}

export const STAGE_RUN_SAMPLE_SUMMARY_SHAPE = PropTypes.shape({
  // New samples don't have an id
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  // If the user doesn't have permissions for the batch or it was deleted, batch and url are undefined.
  batch: BATCH_SUMMARY_SHAPE,
  fileId: PropTypes.string,
  containerId: PropTypes.string,
  url: PropTypes.string,
});

export const STAGE_RUN_SAMPLE_FULL_SHAPE = STAGE_RUN_SAMPLE_SUMMARY_SHAPE;

export const STAGE_RUN_STATUS_SHAPE = PropTypes.oneOf(Object.values(STAGE_RUN_STATUS));

export const STAGE_RUN_SUMMARY_SHAPE = PropTypes.shape({
  createdAt: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface StageEntrySummary {
  id: string;
  editURL: string;
  created_at: string;
  humanId: string | null;
  modified_at: string;
  name: string;
  permissions: Permissions;
  entryDays: Array<EntryDaySummary>;
  tags: Array<Tag>;
  review?: Review;
}

export const STAGE_ENTRY_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  editURL: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface WorkflowTemplateSummary {
  id: string;
  name: string;
}

export const WORKFLOW_TEMPLATE_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface ExperimentalConditionSummary {
  id: string;
  name: string;
}

export const EXPERIMENTAL_CONDITION_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface ExperimentalConditionValue {
  id?: string;
  name: string;
  textValue: string;
  experimentalCondition?: ExperimentalConditionSummary;
}

export const EXPERIMENTAL_CONDITION_VALUE_SHAPE = PropTypes.shape({
  id: PropTypes.string, // Null value indicates condition value has not been saved
  name: PropTypes.string.isRequired,
  textValue: PropTypes.string.isRequired,
  // Null value indicates condition value has not been saved
  experimentalCondition: EXPERIMENTAL_CONDITION_SUMMARY_SHAPE,
});

export interface ExperimentalCondition {
  id: string | null;
  name: string;
  values: Array<ExperimentalConditionValue>;
}

export const EXPERIMENTAL_CONDITION_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string, // Null value indicates condition has not been saved
  name: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(EXPERIMENTAL_CONDITION_VALUE_SHAPE.isRequired).isRequired,
});

export interface StageConfigurationSummary {
  id: string;
  entryTemplateName: string;
  stageName: string;
  index: number;
}

export const STAGE_CONFIGURATION_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  entryTemplateName: PropTypes.string.isRequired,
  stageName: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
});

export interface StageFlowNodeSummary {
  id: string;
}

export const STAGE_FLOW_NODE_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
});

export interface WorkflowTemplateVersionSummary {
  id: string;
  description: string | null;
  modifiedAt: string;
  version: number | null;
  workflowTemplate: WorkflowTemplateSummary | null;
}

export const WORKFLOW_TEMPLATE_VERSION_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  description: PropTypes.string,
  version: PropTypes.number,
  workflowTemplate: WORKFLOW_TEMPLATE_SUMMARY_SHAPE,
});

export interface StageConfiguration extends StageConfigurationSummary {
  experimentalConditions: Array<ExperimentalCondition>;
  stageFlowNodes: Array<StageFlowNodeSummary>;
  workflowTemplateVersion: WorkflowTemplateVersionSummary;
  permissions: Permissions;
  tags: Array<Tag>;
  tagSchema: TagSchema | null;
}

export const STAGE_CONFIGURATION_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  entryTemplateName: PropTypes.string.isRequired,
  stageName: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  experimentalConditions: PropTypes.arrayOf(EXPERIMENTAL_CONDITION_FULL_SHAPE).isRequired,
  stageFlowNodes: PropTypes.arrayOf(STAGE_FLOW_NODE_SUMMARY_SHAPE.isRequired).isRequired,
  workflowTemplateVersion: WORKFLOW_TEMPLATE_VERSION_SUMMARY_SHAPE.isRequired,
});

export interface WorkflowStageSummary {
  id: string;
  name: string;
}

export const WORKFLOW_STAGE_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export const SOURCE_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  rootFolder: PropTypes.shape({
    api_identifier: PropTypes.string.isRequired,
  }).isRequired,
});

export interface WorkflowSummary {
  id: string;
  name: string;
  humanId: string;
  source: ProjectSummary;
  folder?: FolderSummary | null;
  description: string | null;
  createdAt: string;
  modifiedAt: string;
  authors: Array<User>;
  editURL: string;
}

export const WORKFLOW_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  humanId: PropTypes.string.isRequired,
  source: SOURCE_SUMMARY_SHAPE.isRequired,
  description: PropTypes.string,
  createdAt: PropTypes.string.isRequired,
  modifiedAt: PropTypes.string.isRequired,
  authors: PropTypes.arrayOf(PropTypes.shape(USER_SHAPE_PROPS).isRequired).isRequired,
});

export interface RegistrationRecord {
  id: string;
  stageRunId: string;
  stageRunSampleId: string;
}

export const REGISTRATION_RECORD_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  stageRunId: PropTypes.string.isRequired,
  stageRunSampleId: PropTypes.string.isRequired,
});

export interface RegistrationRecordList {
  id: string;
  registrationRecords: Array<RegistrationRecord>;
}

export const REGISTRATION_RECORD_LIST_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  registrationRecords: PropTypes.arrayOf(REGISTRATION_RECORD_SHAPE).isRequired,
});

export const TRANSFER_TABLE_ROW_SHAPE = PropTypes.shape({
  stageRunId: PropTypes.string.isRequired,
  stageRunSampleId: PropTypes.string.isRequired,
});

export interface InputTableConfig {
  sortByType: INPUT_TABLE_SORT_BY_TYPE;
  sortByExperimentalConditionId: string | null;
  isSortReversed: boolean | null;
}

export const INPUT_TABLE_CONFIG_SHAPE = PropTypes.shape({
  sortByType: PropTypes.oneOf(Object.values(INPUT_TABLE_SORT_BY_TYPE)).isRequired,
  sortByExperimentalConditionId: PropTypes.string,
  isSortReversed: PropTypes.bool,
});

export interface ServerStageEntry extends StageEntrySummary {
  stageRuns: Array<StageRunSummary>;
  workflowId: string;
  workflowStageId: string;
  inputTableConfig: InputTableConfig;
  review?: Review;
  tags: Array<FileConstants.SchemaTag>;
  tagSchema: TagSchema | null;
  source: SourceSummary;
  authors: Array<User>;
}

export interface ServerStageEntryDocument extends ServerStageEntry {
  entryVersion: ServerEntryVersion<FromServerId>;
  latestVersionId: number;
}

// Client-side Stage Entry - has cid but not id
export interface StageEntryDocument extends ServerStageEntry {
  latestVersionId: number;
  entryVersion: EntryVersion;
}

export const STAGE_ENTRY_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  stageRuns: PropTypes.arrayOf(STAGE_RUN_SUMMARY_SHAPE).isRequired,
  workflowId: PropTypes.string.isRequired,
  workflowStageId: PropTypes.string.isRequired,
  inputTableConfig: INPUT_TABLE_CONFIG_SHAPE.isRequired,
  permissions: PERMISSIONS_SHAPE.isRequired,
});

export interface WorkflowTemplate extends WorkflowTemplateSummary {
  workflowTemplateVersions: Array<WorkflowTemplateVersionSummary>;
  permissions: Permissions;
  prefix: string;
  owner: Owner;
  ownerInfo: OwnerInfo;
}

export const WORKFLOW_TEMPLATE_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  workflowTemplateVersions: PropTypes.arrayOf(WORKFLOW_TEMPLATE_VERSION_SUMMARY_SHAPE).isRequired,
  permissions: PERMISSIONS_SHAPE.isRequired,
  prefix: PropTypes.string.isRequired,
});

export interface WorkflowStage {
  id: string;
  name: string;
  stageConfiguration: StageConfigurationSummary;
  stageEntries: Array<StageEntrySummary>;
  permissions: Permissions;
}

export const WORKFLOW_STAGE_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  stageConfiguration: STAGE_CONFIGURATION_SUMMARY_SHAPE.isRequired,
  stageEntries: PropTypes.arrayOf(STAGE_ENTRY_SUMMARY_SHAPE.isRequired).isRequired,
  permissions: PERMISSIONS_SHAPE.isRequired,
});

export interface StageFlowNode extends StageFlowNodeSummary {
  parentIds: Array<string>;
  experimentalConditionValues: Array<ExperimentalConditionValue>;
}

export const STAGE_FLOW_NODE_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  parentIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  experimentalConditionValues: PropTypes.arrayOf(EXPERIMENTAL_CONDITION_VALUE_SHAPE),
});

export interface StageRunSummary {
  createdAt: string;
  id: string;
  name: string;
}

export interface StageRun {
  createdAt: string;
  id: string;
  name: string;
  status: STAGE_RUN_STATUS;
  inputSampleIds: Array<string>;
  outputSampleIds: Array<string>;
  stageEntry: StageEntrySummary;
  // TODO(ala) DEPRECATED - don't use!
  parent?: StageRunSummary | null;
  stageFlowNode: StageFlowNode;
}

export const STAGE_RUN_FULL_SHAPE = PropTypes.shape({
  createdAt: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: STAGE_RUN_STATUS_SHAPE.isRequired,
  inputSampleIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  outputSampleIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  stageEntry: STAGE_ENTRY_SUMMARY_SHAPE.isRequired,
  // TODO(ala) DEPRECATED - don't use!
  parent: STAGE_RUN_SUMMARY_SHAPE,
  stageFlowNode: STAGE_FLOW_NODE_FULL_SHAPE.isRequired,
});

export interface WorkflowTemplateVersion extends WorkflowTemplateVersionSummary {
  stageConfigurations: Array<StageConfigurationSummary>;
  permissions: Permissions;
}

export const WORKFLOW_TEMPLATE_VERSION_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  version: PropTypes.number,
  workflowTemplate: WORKFLOW_TEMPLATE_SUMMARY_SHAPE,
  stageConfigurations: PropTypes.arrayOf(STAGE_CONFIGURATION_SUMMARY_SHAPE).isRequired,
  permissions: PERMISSIONS_SHAPE.isRequired,
});

export interface InitializationDataSummary {
  id: string;
  requestFulfillmentId: string | null;
}

export const INITIALIZATION_DATA_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  requestFulfillmentId: PropTypes.string,
});

export interface Workflow extends WorkflowSummary {
  baseWorkflowTemplateVersionId: string | null;
  initializationDatas: Array<InitializationDataSummary>;
  workflowTemplateVersion: WorkflowTemplateVersionSummary;
  workflowStages: Array<WorkflowStageSummary>;
  permissions: Permissions;
}

export const WORKFLOW_FULL_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  humanId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  baseWorkflowTemplateVersionId: PropTypes.string,
  initializationDatas: PropTypes.arrayOf(INITIALIZATION_DATA_SUMMARY_SHAPE.isRequired).isRequired,
  workflowTemplateVersion: WORKFLOW_TEMPLATE_VERSION_SUMMARY_SHAPE.isRequired,
  workflowStages: PropTypes.arrayOf(WORKFLOW_STAGE_SUMMARY_SHAPE).isRequired,
  permissions: PERMISSIONS_SHAPE.isRequired,
});

export interface WorkflowStructuredDataRow {
  stageRunId: string;
  stageRunSampleId: string;
  fields: Array<{
    tableName: string;
    name: string;
    value: string | null;
    important: boolean;
  }>;
}

export const WORKFLOW_STRUCTURED_DATA_ROW_SHAPE = PropTypes.shape({
  stageRunId: PropTypes.string.isRequired,
  stageRunSampleId: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      tableName: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      value: PropTypes.string,
      important: PropTypes.bool.isRequired,
    }).isRequired
  ).isRequired,
});

export enum WorkflowStatus {
  INITIALIZED = 'INITIALIZED',
  COMPLETED = 'COMPLETED',
  DRAFT = 'DRAFT',
}

export const WORKFLOW_STATUS_SHAPE = PropTypes.oneOf([
  STAGE_RUN_STATUS.INITIALIZED,
  STAGE_RUN_STATUS.COMPLETED,
  WORKFLOW_STATUS_DRAFT,
]);

export interface WorkflowDashboardDetails {
  status: WorkflowStatus;
  lastStageCompletionChange: {
    stageName: string;
    lastStageCompletedAt: Optional<string>;
  };
}

export const WORKFLOW_DASHBOARD_DETAILS_SHAPE = PropTypes.shape({
  status: WORKFLOW_STATUS_SHAPE.isRequired,
  lastStageCompletionChange: PropTypes.shape({
    stageName: PropTypes.string.isRequired,
    lastStageCompletedAt: PropTypes.string,
  }),
});

export type WorkflowStructuredData = Array<WorkflowStructuredDataRow>;

export const WORKFLOW_STRUCTURED_DATA_SHAPE = PropTypes.arrayOf(WORKFLOW_STRUCTURED_DATA_ROW_SHAPE);

export interface StageRunSampleKey {
  stageRunId: string;
  stageRunSampleId: string;
}

export interface StageRunSampleDetails {
  stageRun: StageRun;
  stageRunSample: StageRunSample;
}

export const STAGE_RUN_SAMPLE_DETAILS = PropTypes.shape({
  stageRun: STAGE_RUN_FULL_SHAPE.isRequired,
  stageRunSample: STAGE_RUN_SAMPLE_FULL_SHAPE.isRequired,
});

export const WORKFLOW_ENTRY_TEMPLATE_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  entryTemplateName: PropTypes.string.isRequired,
  // permissions property is required for WorkflowEntryTemplateModal
  permissions: PERMISSIONS_SHAPE.isRequired,
});

export interface StageEntrySummaryData {
  hasImportant: boolean;
}

export const STAGE_ENTRY_SUMMARY_DATA = PropTypes.shape({
  hasImportant: PropTypes.bool.isRequired,
});

// Data sources
export const INPUT_SAMPLES_SOURCE_UUID = 'INPUT_SAMPLES_SOURCE_UUID';
export const INPUT_SAMPLES_SOURCE_NAME = 'Input samples';

// TODO: Move SampleGroup related constants out of WorkflowConstants (they're now considered samples models,
// not workflows models).

export interface SampleGroupSchema extends AnonymousSchema {
  allFields: Array<SampleLinkSchemaField>;
  fields: Array<SampleLinkSchemaField>;
}

export const SAMPLE_GROUP_SCHEMA_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  allFields: PropTypes.arrayOf(SCHEMA_FIELD_SHAPE.isRequired).isRequired,
  fields: PropTypes.arrayOf(SCHEMA_FIELD_SHAPE.isRequired).isRequired,
});

export interface BaseSampleGroup {
  tags: Array<FileConstants.SchemaTag>;
}

export interface SampleGroup extends BaseSampleGroup {
  id: string;
}

export interface UISampleGroup extends BaseSampleGroup {
  id?: string;
  cid: string;
}

export interface SampleGroupUpdate {
  cid: string;
  tags: TagUpdateObject;
}

export const SAMPLE_GROUP_SHAPE = PropTypes.shape({
  id: PropTypes.string,
  tags: PropTypes.arrayOf(FileConstants.TAG_SHAPE.isRequired).isRequired,
});

// Keep in sync with workflow_details.py:get_workflow_template_version_details_dict()
export interface WorkflowTemplateVersionDetails {
  workflowTemplateVersion: WorkflowTemplateVersion;
  stageConfigurations: {[stageConfigId: string]: StageConfiguration};
  stageFlowNodes: {[stageFlowNodeId: string]: StageFlowNode};
}

// Keep in sync with workflow_details.py:get_workflow_details_dict()
export interface WorkflowDetails extends WorkflowTemplateVersionDetails {
  workflow: Workflow;
  workflowStages: {[stageId: string]: WorkflowStage};
  stageEntries: {[stageEntryId: string]: ServerStageEntry};
  stageRuns: {[stageRunId: string]: StageRun};
  entryIdToOrderedInputSampleKeys: {[stageEntryId: string]: Array<StageRunSampleKey>};
  entryIdToOrderedOutputSampleKeys: {[stageEntryId: string]: Array<StageRunSampleKey>};
  // TODO(ala): entryIdToStructuredData is temporarily optional until we remove it entirely
  entryIdToStructuredData?: {[stageEntryId: string]: StructuredData};
  // TODO(ala): entryIdToSummaryData is temporarily optional until we remove it entirely
  entryIdToSummaryData?: {[stageEntryId: string]: {hasImportant: boolean}};
}

// Keep in sync with workflow_details.py:get_stage_entry_details_dict()
export interface StageEntryDetails {
  stageEntry: StageEntryDocument;
  stageRuns: {[stageRunId: string]: StageRun};
  stageFlowNodes: {[stageFlowNodeId: string]: StageFlowNode};
  registrationRecordListsById: {[registrationRecordListId: string]: RegistrationRecordList};
  transferOperationListsById: {[transferOperationListId: string]: TransferOperationList};
  transferOperationsById: {[transferOperationId: string]: TransferOperation};
  inputSamplesOrder: Array<StageRunSampleKey>;
  outputSamplesOrder: Array<StageRunSampleKey>;
  workflowStageOrderedStageRunIds: Array<string>;
}

export const WorkflowPreferences = {
  EXTERNAL_INPUT_PICKER_INPUT_TYPE: 'workflows.externalInputPicker.inputType',
  REGISTRATION_TABLE_SAMPLE_TYPE: 'workflows.registrationTable.sampleType',
  SELECT_PARENT_GRID_MODAL_SELECTED_CREATE_TYPE: 'workflows.selectParentGridModal.selectedCreateType',
  SELECT_PARENT_GRID_MODAL_SELECTED_DESTINATION: 'workflows.selectParentGridModal.selectedDestination',
};
